// web/src/tutorial/quickstart/zheng-drift.ts
// ZhengDrift · Quick Start（最短流程：选演奏者 → 选 MIDI → 生成动画）
// 依据：G:\zheng_drift_rust
//   src/ui/app.rs（顶栏四个标签页与顺序）、avatar_panel.rs / avatar_display.rs、midi_panel.rs、
//   execute_panel.rs、technique_editor.rs、data.rs（参数取值范围与默认值）、zheng_drifter.rs（输出文件命名）
//   界面原文：assets/i18n/zh-CN.json、assets/i18n/en-US.json（UI 名称以此为准，含 emoji）
// 参数逐个讲解放在 detail/zheng-drift.ts，本页只保留最短跑通流程。

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const zhengDriftQuickstart: TutorialDoc = {
  key: "zheng_drift",
  slug: "zheng-drift",
  kind: "quickstart",
  icon: "🪕",
  title: l("ZhengDrift 古筝演奏动画", "ZhengDrift Guzheng Performance"),
  subtitle: l(
    "最短流程：选一个演奏者 → 选一份 MIDI → 点一下生成，拿到能直接加载进 Blender / Unreal 的古筝演奏动画。全部参数保持默认就好。",
    "The shortest path: pick a performer, pick a MIDI file, generate — and get guzheng performance animation you can load straight into Blender or Unreal. Leave every parameter at its default.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV13Z7v6AEeN/",
    youtube: "https://www.youtube.com/watch?v=QMXpcwRGOfs",
    caption: l(
      "ZhengDrift 完整操作演示",
      "ZhengDrift full walkthrough",
    ),
    series: [
      {
        title: l(
          "如何在虚幻引擎中弹古筝 第0集 简介",
          "How To Play GuZheng In Unreal Episode 0: Introduction",
        ),
        url: "https://www.bilibili.com/video/BV1GFTQ6SE7E/",
        youtube: "https://www.youtube.com/watch?v=1PF-218E4KU",
      },
      {
        title: l(
          "如何在虚幻引擎中弹古筝 第6集 生成动画",
          "How To Play GuZheng In Unreal Episode 6: Generate Animation",
        ),
        url: "https://www.bilibili.com/video/BV1FwgZ6WE3p/",
        youtube: "https://www.youtube.com/watch?v=HK1lzEViWcU",
      },
      {
        title: l("ZhengDrift 跑通 Unreal", "ZhengDrift in Unreal end to end"),
        url: "https://www.bilibili.com/video/BV1xMDxB6Epf/",
        youtube: "https://www.youtube.com/watch?v=Fv6aN7O_-fg",
      },
    ],
  },
  images: [
    {
      src: "/assets/image/products/zheng-drift/CN/参数设置.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Parameters.png",
      label: l("参数设置", "Parameters"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/MIDI信息.png",
      srcEn: "/assets/image/products/zheng-drift/EN/MIDI.png",
      label: l("MIDI 信息", "MIDI"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/技法编辑.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Technique Editor.png",
      label: l("技法编辑", "Technique"),
      width: 822,
      height: 852,
    },
    {
      src: "/assets/image/products/zheng-drift/CN/生成动画.png",
      srcEn: "/assets/image/products/zheng-drift/EN/Animation.png",
      label: l("生成动画", "Animation"),
      width: 822,
      height: 852,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "ZhengDrift 把一份 MIDI 变成**古筝演奏动画**：哪根手指拨哪根弦、手掌怎么横向走、身体朝哪边转，全部算好写进数据文件，你在 Blender / Unreal 里加载就能播放、渲染。整个过程不需要你在 3D 软件里手动摆一帧。",
            "ZhengDrift turns a MIDI file into **guzheng performance animation**: which finger plucks which string, how the palm travels along the strings, which way the body turns. All of it is computed and written to data files that you load in Blender or Unreal and simply play or render. You never hand-key a frame.",
          ),
        },
        {
          kind: "p",
          text: l(
            "最短流程只有三步：**选演奏者 → 选 MIDI → 生成动画**。参数全部保持默认值，第一次用足够跑出一段像样的演奏。",
            "The shortest path has three steps: **pick a performer → pick a MIDI → generate**. Leave every parameter at its default; for a first run that is more than good enough.",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("① 选演奏者", "① Pick a performer"),
              text: l(
                "在 `参数设置` 页的下拉菜单里选一个人物。软件自带示例演奏者，不用自己准备。",
                "Choose a character from the dropdown on the `参数设置` / Parameters tab. Example performers ship with the app — you do not need to supply one.",
              ),
            },
            {
              title: l("② 选 MIDI", "② Pick a MIDI"),
              text: l(
                "在 `MIDI 信息` 页选文件、`🔍 扫描 MIDI 信息`，勾上要生成动画的轨道再 `✅ 添加选中的轨道`。",
                "On the `MIDI 信息` / MIDI tab, load the file, click `🔍 扫描 MIDI 信息` / 🔍 Scan MIDI Info, tick the tracks you want animated and click `✅ 添加选中的轨道` / ✅ Add Selected Tracks.",
              ),
            },
            {
              title: l("③ 生成动画", "③ Generate"),
              text: l(
                "在 `生成动画` 页点 `⚡ 一键执行`，等控制台跑完出现「动画生成完成」。",
                "On the `生成动画` / Animation tab click `⚡ 一键执行` / ⚡ Run All and wait for `动画生成完成` / Animation generation complete in the console.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "想调手感、想让某段摇指/琶音换一种演奏方式，再看[软件界面详细说明](/details/zheng-drift.html)——那里逐个参数讲清楚了「调什么、什么时候调、调完会看到什么」。",
            "When you want to shape the motion, or make a tremolo / arpeggio passage play differently, head to the [UI Reference](/details/zheng-drift.html) — it explains every parameter: what it does, when to touch it, and what changes when you do.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前", "Before you start"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "只需要准备**一份 MIDI 文件**。演奏者是软件自带的，在 `参数设置` 页的下拉菜单里直接选；想把你自己的人物做成可用演奏者，那是进阶话题，先跑通这一遍再说。",
            "All you need is **one MIDI file**. Performers ship with the app — just pick one from the dropdown on the `参数设置` / Parameters tab. Turning your own character into a performer is an advanced topic; get a run through first.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "**MIDI 文件**：扩展名 `.mid` 或 `.midi`，放在哪儿都行，程序里手动选或者直接拖进窗口。第一次建议用古筝独奏、轨道少一点的文件。",
              "**A MIDI file**: `.mid` or `.midi`, anywhere on disk — pick it in the app or drag it into the window. For a first run, a solo guzheng piece with few tracks is easiest.",
            ),
            l(
              "**运行目录可写**：生成结果写在程序自己的 `output/` 里，别把程序放在没有写入权限的位置（比如某些系统目录）。",
              "**A writable app folder**: results go into the app's own `output/` folder, so avoid installing it somewhere without write permission.",
            ),
            l(
              "**已经装好 MusicDoll 插件**：结果要在 Blender 或 Unreal 里加载，插件免费，装一次就行。",
              "**The MusicDoll plugin installed**: you load the result in Blender or Unreal; the plugin is free and only needs installing once.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：四个页签按顺序点", "UI overview: four tabs, in order"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "窗口很小，只有顶栏四个页签。跑动画要用的是前三个，`技法编辑` 是跑完之后回头看结果的工具。",
            "The window is small and has just four tabs in the title bar. The first three are what you need; `技法编辑` / Technique is a tool you come back to after a run.",
          ),
        },
        {
          kind: "table",
          head: [
            l("页签", "Tab"),
            l("干什么", "What it is for"),
            l("跑动画时", "In the main flow"),
          ],
          rows: [
            [
              l("`参数设置`", "`Parameters`"),
              l(
                "选演奏者，以及动作、记录池、技法检测等参数。",
                "Pick the performer, plus the motion, pool and technique-detection parameters.",
              ),
              l("第 1 步（只选人）", "Step 1 — just pick someone"),
            ],
            [
              l("`MIDI 信息`", "`MIDI`"),
              l(
                "选 MIDI 文件、扫描轨道、勾选要生成动画的轨道。",
                "Load the MIDI, scan its tracks, tick the tracks to animate.",
              ),
              l("第 2 步", "Step 2"),
            ],
            [
              l("`生成动画`", "`Animation`"),
              l(
                "点生成，右侧控制台输出全过程日志。",
                "Press generate; the log column on the right streams the whole run.",
              ),
              l("第 3 步", "Step 3"),
            ],
            [
              l("`技法编辑`", "`Technique`"),
              l(
                "列出识别出的摇指 / 琶音片段，可以把不想要的转成普通音符。",
                "Lists the detected tremolo / arpeggio passages so you can turn unwanted ones back into normal notes.",
              ),
              l("可选，跑完再看", "Optional, after the run"),
            ],
          ],
        },
        {
          kind: "note",
          text: l(
            "顶栏右上角还有 `语言` 菜单、`帮助` 菜单和 `暗色主题` 勾选框，跟生成结果无关；参数改动不会自动保存，重开软件回到默认值。",
            "The top-right corner also holds the `语言` / Language menu, the `帮助` / Help menu and the `暗色主题` / Dark theme checkbox; none of them affect the output. Parameter changes are not saved, so they reset when you restart.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选演奏者", "Step 1 — pick a performer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `参数设置` 页签，左上第一张卡片 `01 角色选择` 里有个下拉菜单，点开就是软件自带的演奏者列表。",
            "Open the `参数设置` / Parameters tab. The first card, `01 角色选择` / Avatar Selection, holds a dropdown listing the performers that ship with the app.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点开下拉菜单，选一个人物（例如 `钟离Unreal`）。",
              "Open the dropdown and pick a character (for example `钟离Unreal`).",
            ),
            l(
              "看右边的 `02 角色信息` 卡片，确认显示的人物图片就是你想用的那位。",
              "Check the `02 角色信息` / Avatar Info card on the right: the portrait shown should be the character you meant.",
            ),
            l(
              "**下面所有参数都不用动。** `03 动作参数`、`04 记录池设置`、`技法检测`、`演奏技法` 用默认值就能出片。",
              "**Leave every other control alone.** `03 动作参数` / Motion Parameters, `04 记录池设置` / Recorder Pool, `技法检测` / Technique Detection and `演奏技法` / Performance Techniques all work at their default values.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "想细调手感（拨弦快慢、手掌移动速度、抬掌幅度、摇指/琶音判定松紧……），看[软件界面详细说明](/details/zheng-drift.html)里 `参数设置` 那一节。",
            "To fine-tune the feel — pluck timing, how fast the palm may travel, lift height, how eagerly tremolo / arpeggio are detected — read the `参数设置` / Parameters section of the [UI Reference](/details/zheng-drift.html).",
          ),
        },
        {
          kind: "note",
          text: l(
            "`✏ 修改` / `➕ 新增` / `🗑 删除` 三个按钮用来管理演奏者列表，跟跑动画无关；列表为空时左栏会出现一个 `➕ 新增角色` 按钮。",
            "The `✏ 修改` / ✏ Edit / `➕ 新增` / ➕ Add / `🗑 删除` / 🗑 Delete buttons manage the performer list and have nothing to do with generating; with an empty list the left card shows a `➕ 新增角色` / ➕ New Avatar button instead.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI 与轨道", "Step 2 — pick the MIDI and its tracks"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `MIDI 信息` 页签。左边是文件，右边是轨道；`02 参数设置` 卡片里的 `使用通道 (-1=所有):`、`FPS:`、`音符量化:` 都不用动。",
            "Open the `MIDI 信息` / MIDI tab: files on the left, tracks on the right. You do not need to touch `使用通道 (-1=所有):` / Use channel (-1=all):, `FPS:` or `音符量化:` / Note quantization: in the `02 参数设置` / Parameters card.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "在 `01 MIDI 文件管理` 卡片里点 `📂 选择 MIDI 文件` 挑文件；也可以把 `.mid` / `.midi` 直接拖到卡片下方的虚线框 `📥 将 MIDI 文件拖放到此处`。选好后 `当前文件:` 会显示文件名。",
              "In the `01 MIDI 文件管理` / MIDI File Management card click `📂 选择 MIDI 文件` / 📂 Select MIDI File to browse, or drop a `.mid` / `.midi` onto the dashed `📥 将 MIDI 文件拖放到此处` / 📥 Drop a MIDI file here box below. Once loaded, `当前文件:` / Current file: shows the file name.",
            ),
            l(
              "点 `🔍 扫描 MIDI 信息`。右边的 `03 轨道选择` 会列出每条有音符的轨道，每行是 `轨道` / `通道` / `乐器` / `音符数` 四列。",
              "Click `🔍 扫描 MIDI 信息` / 🔍 Scan MIDI Info. The `03 轨道选择` / Track Selection card on the right then lists every track that contains notes, with `轨道` / Track / `通道` / Channel / `乐器` / Instrument / `音符数` / Notes columns.",
            ),
            l(
              "勾上要生成古筝动画的轨道——**通常是那条古筝/主旋律轨**，看 `乐器` 和 `音符数` 判断，别选鼓轨或铺底伴奏。",
              "Tick the track you want played — **usually the guzheng / melody track**, judged by its `乐器` / Instrument and `音符数` / Notes. Avoid drum tracks and backing pads.",
            ),
            l(
              "点 `✅ 添加选中的轨道`。左下 `02 参数设置` 卡片里的 `已选择的轨道:` 会出现 `轨道 1` 这样的标签，说明确实加进去了。",
              "Click `✅ 添加选中的轨道` / ✅ Add Selected Tracks. The `已选择的轨道:` / Selected tracks: line in the `02 参数设置` / Parameters card below then shows a tag such as `轨道 1` / Track 1, confirming it was accepted.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("最容易漏的一步", "The step people miss"),
          text: l(
            "勾选复选框**不会**立刻生效，必须再点一次 `✅ 添加选中的轨道`。漏点这一步，生成时会报「错误：未选择 MIDI 轨道」。",
            "Ticking a checkbox does **not** take effect by itself — you must click `✅ 添加选中的轨道` / ✅ Add Selected Tracks afterwards. Skip it and generation stops with `错误：未选择 MIDI 轨道` / Error: no MIDI track selected.",
          ),
        },
        {
          kind: "note",
          text: l(
            "同一屏里的 `✂️ 修剪 MIDI 音符` 会把所选轨道的音符另外存成一份精简 MIDI，属于排查工具，第一次跑不需要点。",
            "Also on this screen, `✂️ 修剪 MIDI 音符` / ✂️ Trim MIDI Notes writes a slimmed-down MIDI containing only the selected tracks' notes. It is a troubleshooting tool; you do not need it on a first run.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：生成动画", "Step 3 — generate"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `生成动画` 页签。左边 `01 配置概览` 先把当前设置回显一遍，右边 `04 控制台输出` 是日志。",
            "Open the `生成动画` / Animation tab. `01 配置概览` / Configuration Overview on the left echoes the current setup; `04 控制台输出` / Console Output on the right is the log.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "瞄一眼 `01 配置概览`：`当前演奏者`、`当前 MIDI`、`轨道`、`通道`、`帧率`。有任何一项不对就回上一步改。",
              "Glance at `01 配置概览` / Configuration Overview: `当前演奏者` / Current performer, `当前 MIDI` / Current MIDI, `轨道` / Track, `通道` / Channel, `帧率` / FPS. If any of them is wrong, go back one step.",
            ),
            l(
              "点 `03 执行操作` 卡片里的 `⚡ 一键执行`（在 `快速执行` 分组下）。它会依次跑完解析乐曲 → 生成指法 → 生成动画。",
              "Click `⚡ 一键执行` / ⚡ Run All in the `03 执行操作` / Operations card (under `快速执行` / Quick Run). It runs parse music → generate fingering → generate animation in sequence.",
            ),
            l(
              "等控制台停住。期间会看到 `阶段 1` 到 `阶段 5` 的进度行、`指法生成进度：(x/y)`；跑完出现绿字「动画生成完成」并给出输出路径，左侧状态从 `状态：运行中` 变成 `状态：空闲`。",
              "Wait for the log to settle. Along the way you will see the `阶段 1` / Stage 1 … `阶段 5` / Stage 5 progress lines and `指法生成进度：(x/y)` / Fingering progress: (x/y); at the end a green `动画生成完成` / Animation generation complete line prints the output path and the badge flips from `状态：运行中` / Status: running to `状态：空闲` / Status: idle.",
            ),
            l(
              "点控制台上方的 `复制结果`，主配置文件 `_config.zhengdrift` 的完整路径就进了剪贴板——下一步插件要的就是它。",
              "Click `复制结果` / Copy Result above the log to copy the full path of the `_config.zhengdrift` file — that is exactly what the plugin needs next.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "想看着每一步分别成功再往下走，就别用一键执行，改点 `分步执行` 里的 `🎵 1. 解析乐曲` → `🎼 2. 生成指法` → `🎹 3. 生成动画`。顺序不能颠倒：后一步要读前一步写出的中间文件。",
            "If you prefer to watch each stage succeed before continuing, skip the one-click button and use `分步执行` / Step by Step: `🎵 1. 解析乐曲` / 🎵 1. Parse Music → `🎼 2. 生成指法` / 🎼 2. Generate Fingering → `🎹 3. 生成动画` / 🎹 3. Generate Animation. The order is fixed — each stage reads what the previous one wrote.",
          ),
        },
        {
          kind: "note",
          text: l(
            "跑的过程中想停就点 `❌ 取消`；控制台的 `复制全部` / `清空信息` 分别是拷日志和清日志。",
            "While a run is in progress you can press `❌ 取消` / ❌ Cancel. `复制全部` / Copy All and `清空信息` / Clear copy or clear the log.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成出来的是什么，怎么用", "What came out, and how to use it"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "一次成功的生成会在程序的 `output/` 目录里写下一组文件，文件名是 `{演奏者}_{MIDI名}_{轨道}` 打头。真正要交给插件的是 **`_config.zhengdrift`** 这一个，其余几个它自己会顺着路径去读。",
            "A successful run writes a set of files into the app's `output/` folder, all named `{performer}_{midi}_{tracks}`. The one you hand to the plugin is **`_config.zhengdrift`**; it pulls in the others by path.",
          ),
        },
        {
          kind: "table",
          head: [
            l("文件", "File"),
            l("里面是什么", "What is in it"),
          ],
          rows: [
            [
              l("`..._config.zhengdrift`", "`..._config.zhengdrift`"),
              l(
                "主配置：帧率、总帧数、时间参数，以及另外三个 `.zhengdrift` 与活动曲线的绝对路径。**插件加载的就是它。**",
                "The main report: FPS, total frames, timing parameters, and the absolute paths of the other three `.zhengdrift` files plus the activity curve. **This is what the plugin loads.**",
              ),
            ],
            [
              l("`..._performance_anim.zhengdrift`", "`..._performance_anim.zhengdrift`"),
              l(
                "演奏者动画：左右手的手指动作与手掌位移关键帧。",
                "Performer animation: left/right hand finger actions and palm-travel keyframes.",
              ),
            ],
            [
              l("`..._target_anim.zhengdrift`", "`..._target_anim.zhengdrift`"),
              l(
                "身体朝向动画：由双手位置驱动的转身 / 转胸 / 转头目标。",
                "Body orientation: body / chest / head turn targets driven by the hands' position.",
              ),
            ],
            [
              l("`..._string_anim.zhengdrift`", "`..._string_anim.zhengdrift`"),
              l(
                "琴弦动画事件：哪根弦在什么时候被按下、被拨动。",
                "String events: which string is pressed or plucked, and when.",
              ),
            ],
          ],
        },
        {
          kind: "ol",
          items: [
            l(
              "在 Blender 或 Unreal 里启用免费的 MusicDoll 插件，打开古筝演奏者面板。",
              "Enable the free MusicDoll plugin in Blender or Unreal and open its guzheng performer panel.",
            ),
            l(
              "在插件里选中**同一位演奏者**，再加载 `..._config.zhengdrift`。演奏者必须一致，否则骨骼和记录器对不上。",
              "Select the **same performer** in the plugin, then load `..._config.zhengdrift`. The performer must match, or bones and recorders will not line up.",
            ),
            l(
              "插件顺着主配置里的路径读入三个动画文件，写进时间线与 Shape Key，然后就能播放或渲染了。",
              "The plugin follows the paths in the report, loads the three animation files onto the timeline and Shape Keys, and you can play or render.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("别搬动 output 目录", "Do not move the output folder"),
          text: l(
            "`_config.zhengdrift` 里记的是**绝对路径**。把 `output/` 改名或搬走，插件就找不到数据了；要么搬回去，要么重新生成一遍。",
            "The `_config.zhengdrift` stores **absolute paths**. Rename or move `output/` and the plugin can no longer find the data — move it back, or regenerate.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前自检清单", "Pre-flight checklist"),
      image: { image: 3 },
      blocks: [
        {
          kind: "ul",
          items: [
            l(
              "`参数设置` 的下拉菜单里选好了一位演奏者，`02 角色信息` 能看到人物图片。",
              "A performer is selected on `参数设置` / Parameters, and `02 角色信息` / Avatar Info shows a portrait.",
            ),
            l(
              "`MIDI 信息` 的 `当前文件:` 是你要用的那首曲子。",
              "The `当前文件:` / Current file: line on `MIDI 信息` / MIDI shows the piece you want.",
            ),
            l(
              "`已选择的轨道:` 下面至少有一个 `轨道 N` 标签——也就是说，扫完轨道后确实点过 `✅ 添加选中的轨道`。",
              "At least one `轨道 N` / Track N tag appears under `已选择的轨道:` / Selected tracks: — in other words, you really did click `✅ 添加选中的轨道` / ✅ Add Selected Tracks after scanning.",
            ),
            l(
              "`生成动画` 的 `01 配置概览` 里，`当前演奏者` 与 `当前 MIDI` 和你预期的一致。",
              "In `01 配置概览` / Configuration Overview on `生成动画` / Animation, `当前演奏者` / Current performer and `当前 MIDI` / Current MIDI match what you expect.",
            ),
            l(
              "程序目录可写（`output/` 能创建文件），并且你打算在插件里选的是同一位演奏者。",
              "The app folder is writable (so `output/` can be created), and you intend to select the same performer in the plugin.",
            ),
          ],
        },
        {
          kind: "table",
          head: [l("现象", "Symptom"), l("怎么办", "What to do")],
          rows: [
            [
              l(
                "控制台红字「错误：请先在参数设置页选择演奏者文件」",
                "Console: `错误：请先在参数设置页选择演奏者文件` / Error: please select a performer file on the parameter page first",
              ),
              l(
                "回 `参数设置` 选一个演奏者（列表为空就 `➕ 新增` 一个）。",
                "Go back to `参数设置` / Parameters and select one (use `➕ 新增` / ➕ Add if the list is empty).",
              ),
            ],
            [
              l(
                "控制台红字「错误：请先在 MIDI 处理页选择 MIDI 文件」",
                "Console: `错误：请先在 MIDI 处理页选择 MIDI 文件` / Error: please select a MIDI file on the MIDI page first",
              ),
              l(
                "回 `MIDI 信息` 用 `📂 选择 MIDI 文件` 或拖放选一份。",
                "Go back to `MIDI 信息` / MIDI and load one with `📂 选择 MIDI 文件` / 📂 Select MIDI File or by dropping it in.",
              ),
            ],
            [
              l(
                "控制台红字「错误：未选择 MIDI 轨道，请在 MIDI 面板中选择要处理的轨道」",
                "Console: `错误：未选择 MIDI 轨道，请在 MIDI 面板中选择要处理的轨道` / Error: no MIDI track selected, please select tracks in the MIDI panel",
              ),
              l(
                "勾选轨道后补点一次 `✅ 添加选中的轨道`。",
                "Tick the track and then click `✅ 添加选中的轨道` / ✅ Add Selected Tracks.",
              ),
            ],
            [
              l(
                "红字「错误：找不到调式信息文件，请先执行「解析乐曲」步骤」",
                "Console: `错误：找不到调式信息文件，请先执行「解析乐曲」步骤` / Error: tuning info file not found, please run \"Parse Music\" first",
              ),
              l(
                "别用分步执行跳着点，从 `🎵 1. 解析乐曲` 重来；换过 MIDI 或轨道后也要重来。",
                "Do not jump around in `分步执行` / Step by Step; restart from `🎵 1. 解析乐曲` / 🎵 1. Parse Music. Restart it too after changing the MIDI or the tracks.",
              ),
            ],
            [
              l(
                "红字「错误：找不到指法文件 output/recorder/…json」",
                "Console: `错误：找不到指法文件 output/recorder/…json` / Error: fingering file not found output/recorder/…json",
              ),
              l(
                "先跑 `🎼 2. 生成指法`；刚改过 MIDI 或轨道的话，从步骤 1 开始整条重跑。",
                "Run `🎼 2. 生成指法` / 🎼 2. Generate Fingering first; if you just changed the MIDI or tracks, re-run from step 1.",
              ),
            ],
            [
              l(
                "红字「[ERROR] ❌ 未能找到有效的 recorder，池子为空！」",
                "Console: `[ERROR] ❌ 未能找到有效的 recorder，池子为空！` / [ERROR] ❌ No valid recorder found, the pool is empty!",
              ),
              l(
                "多半是轨道选错了：回 `MIDI 信息` 换一条（看 `乐器` 与 `音符数`），别选鼓轨和铺底伴奏。",
                "The track is probably wrong: go back to `MIDI 信息` / MIDI and pick another (check `乐器` / Instrument and `音符数` / Notes) — not drums or pads.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "FAQ"),
      image: { image: 3 },
      blocks: [
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**会改动我的 MIDI 原文件吗？** 不会，程序全程只读 MIDI；只有 `✂️ 修剪 MIDI 音符` 会另存一份 `原文件名_trimmed.mid`。",
            "**Does it modify my MIDI file?** No — the app only reads it. The one exception is `✂️ 修剪 MIDI 音符` / ✂️ Trim MIDI Notes, which saves a separate `<_name>_trimmed.mid`.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**一首曲子要跑多久？** 看音符数和 `FPS:` 而定。生成指法阶段日志会刷 `指法生成进度：(x/y)`，用剩下的数量除一下就能估个大概。",
            "**How long does a piece take?** It depends on the note count and `FPS:`. During fingering the log prints `指法生成进度：(x/y)` / Fingering progress: (x/y), so divide the remaining notes to estimate the wait.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**换了演奏者要整条重跑吗？** 不用。中间文件跟演奏者无关，直接重跑 `🎹 3. 生成动画` 就行；换了 MIDI、通道或轨道才要从 `🎵 1. 解析乐曲` 重来。",
            "**Must I re-run everything after switching performer?** No — the intermediate files do not depend on the performer, so just re-run `🎹 3. 生成动画` / 🎹 3. Generate Animation. Only a change of MIDI, channel or tracks forces a restart from `🎵 1. 解析乐曲` / 🎵 1. Parse Music.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**`技法编辑` 里一条片子都没有，坏了？** 不是，只是这首曲子没检测到摇指或琶音。想确认可以回 `参数设置` 看 `启用摇指检测` / `启用琶音检测` 有没有勾上。",
            "**`技法编辑` / Technique lists nothing at all — is it broken?** No: the piece simply has no detected tremolo or arpeggio. Check that `启用摇指检测` / Enable tremolo detection / `启用琶音检测` / Enable arpeggio detection are ticked on `参数设置` / Parameters.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "**界面语言和主题怎么换？** 顶栏右上角的 `语言` 菜单和 `暗色主题` 勾选框。语言选择会记住，参数改动不会保存。",
            "**How do I change language and theme?** Use the `语言` / Language menu and the `暗色主题` / Dark theme checkbox in the top-right corner. Your language choice is remembered; parameter changes are not saved.",
          ),
        },
      ],
    },
  ],
};
